import {selectJobDetails} from 'components/customer/Confirmation/store/confirmationSlice';
import {useJobContext} from 'contexts/JobContext';
import {Job, PartialJob} from 'shared/types/PartialJob';
import {useAppSelector} from 'store/customer';
import {useCallback} from 'react';
import {JobForm} from 'components/customer/Job/entity/JobForm';
import {useParams} from 'react-router-dom';
import {useJobFormSubmitHandler} from 'hooks';
import {useAppContext} from 'contexts/ApplicationContext';
import {toFormDate} from 'components/customer/Job/helpers/toFormDate';
import {useGetJobPackagingOptionsQuery} from 'components/customer/Job/store/packagingOptionApi';

const normalise = (value?: string | number | null) =>
    value === undefined || value === null ? '' : String(value).trim();

const isDifferent = (a?: string | number | null, b?: string | number | null) =>
    normalise(a) !== normalise(b);

export const hasDeliveryDetailsChanged = (
    job: Partial<Job>,
    jobDetails: JobForm
): boolean => {
    const dispatchMethodChanged = isDifferent(
        job.dispatchMethod,
        jobDetails.dispatch
    );
    const depotChanged = isDifferent(job.depotId, jobDetails.depotId);
    const addressChanged =
        isDifferent(job.address, jobDetails.street) ||
        isDifferent(job.suburb, jobDetails.suburb) ||
        isDifferent(job.city, jobDetails.city) ||
        isDifferent(job.addressState, jobDetails.state) ||
        isDifferent(job.postcode, jobDetails.postcode);

    const dateChanged = isDifferent(
        toFormDate(job.requestedDeliveryDate),
        jobDetails.requestedDeliveryDate
    );

    return (
        dispatchMethodChanged || depotChanged || addressChanged || dateChanged
    );
};

const hasPackagingOptionsChanged = (
    savedOptions: {packaging_option_id: number | null}[],
    selectedIds: number[]
) => {
    const savedIds = savedOptions
        .map((option) => option.packaging_option_id)
        .filter((id): id is number => id !== null)
        .sort((a, b) => a - b);
    const selectedIdsSorted = Array.from(new Set(selectedIds)).sort(
        (a, b) => a - b
    );

    return (
        savedIds.length !== selectedIdsSorted.length ||
        savedIds.some((id) => !selectedIdsSorted.includes(id))
    );
};

export const useSaveJob = () => {
    const {userProfile} = useAppContext();
    const {job} = useJobContext() as PartialJob;
    const jobDetails = useAppSelector(selectJobDetails);
    const {jobId} = useParams();
    const {data: savedOptions} = useGetJobPackagingOptionsQuery(
        {jobId: job?.id ?? 0},
        {skip: !job?.id}
    );
    const {jobFormSubmitHandler} = useJobFormSubmitHandler(
        jobId,
        undefined,
        true
    );

    const saveJob = useCallback(async (): Promise<boolean> => {
        const captureDeliveryDetails =
            userProfile &&
            userProfile?.jobConfirmationEnabled &&
            !userProfile?.capturePickupFreightAtJobCreation;

        if (captureDeliveryDetails) {
            if (!job?.id || !jobDetails) return false;
            if (
                jobDetails.packagingOptionsLoaded !== true ||
                jobDetails.packagingOptionsValid !== true ||
                !savedOptions
            ) {
                return false;
            }

            const deliveryChanged = hasDeliveryDetailsChanged(job, jobDetails);
            const optionsChanged = hasPackagingOptionsChanged(
                savedOptions.options,
                jobDetails.packagingOptionIds ?? []
            );
            if (!deliveryChanged && !optionsChanged) return true;

            return (await jobFormSubmitHandler(jobDetails)) !== false;
        }
        return Promise.resolve(true);
    }, [job, jobDetails, jobFormSubmitHandler, savedOptions, userProfile]);

    return {saveJob};
};
